
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { usePaymentRequest } from "@/hooks/payment/usePaymentRequest";
import CardPaymentForm from "./CardPaymentForm";
import PaymentRequestButton from "./PaymentRequestButton";
import PaymentUnavailableMessage from "./PaymentUnavailableMessage";
import { useEffect, useState } from "react";

interface StripePaymentFormProps {
  onSubmit: (paymentMethod: any) => Promise<void>;
  isProcessing: boolean;
  amount: string;
  paymentMethod?: string;
}

const StripePaymentForm = ({ 
  onSubmit, 
  isProcessing, 
  amount, 
  paymentMethod = "stripe" 
}: StripePaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(true);

  const { paymentRequest, canMakePayment, isProcessing: paymentRequestProcessing } = usePaymentRequest({
    stripe,
    elements,
    amount,
    paymentMethod,
    onSubmit
  });

  // Combined processing state
  const combinedProcessing = isProcessing || paymentRequestProcessing;

  useEffect(() => {
    if (!stripe || !elements) return;
    
    // Add a short delay to allow the payment request to initialize
    const timer = setTimeout(() => {
      setIsLoading(false);
      console.log(`Payment method loaded: ${paymentMethod}, can make payment: ${canMakePayment}`);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [stripe, elements, paymentMethod, canMakePayment]);

  // Log for debugging
  useEffect(() => {
    console.log(`Payment method: ${paymentMethod}, canMakePayment: ${canMakePayment}, isProcessing: ${combinedProcessing}`);
  }, [paymentMethod, canMakePayment, combinedProcessing]);

  // Determine which payment UI to render based on payment method
  const renderPaymentUI = () => {
    if (isLoading) {
      return (
        <div className="text-center p-8">
          <p className="text-gray-500">Loading payment options...</p>
        </div>
      );
    }

    // Handle default card payment method (stripe)
    if (paymentMethod === 'stripe') {
      return (
        <CardPaymentForm 
          amount={amount}
          isProcessing={combinedProcessing}
          stripe={stripe}
          elements={elements}
          onSubmit={onSubmit}
        />
      );
    }

    // For alternative payment methods
    if (['google-pay', 'apple-pay'].includes(paymentMethod)) {
      if (canMakePayment && paymentRequest) {
        return <PaymentRequestButton 
          paymentRequest={paymentRequest} 
          paymentMethod={paymentMethod} 
          isProcessing={combinedProcessing}
        />;
      }
      
      return <PaymentUnavailableMessage paymentMethod={paymentMethod} />;
    }
    
    // Fallback to card element for any other method
    return (
      <CardPaymentForm 
        amount={amount}
        isProcessing={combinedProcessing}
        stripe={stripe}
        elements={elements}
        onSubmit={onSubmit}
      />
    );
  };

  return renderPaymentUI();
};

export default StripePaymentForm;
